





import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import styled from 'styled-components';

import { resetPasswordService, setNewPasswordService } from 'shared/services';

import { colors, fonts } from '../shared/tokens';
import { ArrowRightIcon, EyeIcon, EyeOffIcon } from '../shared/Icons';
import {
  Logo,
  AuthCard,
  CardTitle,
  CardSubtitle,
  FieldGroup,
  FieldLabel,
  InputShell,
  TextInput,
  TrailingButton,
  PrimaryButton,
  AuthorizedNotice,
  StatusFooter,
} from './authShared';
import { validateNewPassword } from './passwordValidation';

const AUTH_BG = '/images/redesign/auth-bg.jpg';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const code = params.get('code');
  const username = params.get('username');
  const showSetPassword = Boolean(code && username);

  return (
    <Page>
      <Backdrop />

      <TopLogo>
        <Logo big href="/" as="a" />
      </TopLogo>

      <CardWrap>
        {showSetPassword ? (
          <SetNewPassword code={code} username={username} />
        ) : (
          <RequestReset />
        )}
      </CardWrap>
    </Page>
  );
};

export default ResetPassword;



const RequestReset = () => {
  const navigate = useNavigate();
  const [account, setAccount] = useState('');
  const [feedback, setFeedback] = useState(null);

  const { mutate, isLoading } = useMutation(resetPasswordService, {
    onSuccess: () =>
      setFeedback({
        type: 'success',
        text: 'If the account exists, reset instructions have been sent.',
      }),
    onError: (err) =>
      setFeedback({
        type: 'error',
        text: err?.message || 'Could not reset password. Please try again.',
      }),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!account.trim()) return;
    setFeedback(null);
    mutate(account);
  };

  return (
    <AuthCard as="form" onSubmit={handleSubmit} $center>
      <LogoLine>
        <Logo />
      </LogoLine>
      <CenteredTitle>Reset Password</CenteredTitle>
      <CenteredSubtitle>Enter account name to reset password</CenteredSubtitle>

      <FieldGroup>
        <FieldLabel htmlFor="reset-username">Username</FieldLabel>
        <InputShell>
          <TextInput
            id="reset-username"
            type="text"
            placeholder="Enter your username"
            value={account}
            onChange={(e) => {
              setAccount(e.target.value);
              setFeedback(null);
            }}
            autoComplete="username"
          />
        </InputShell>
      </FieldGroup>

      {feedback && <FeedbackText $type={feedback.type}>{feedback.text}</FeedbackText>}

      <PrimaryButton type="submit" disabled={isLoading}>
        <ArrowRightIcon size={16} />
        <span>Enter</span>
      </PrimaryButton>

      <BackLink type="button" onClick={() => navigate('/login')}>
        Back to sign in
      </BackLink>

      <AuthorizedNotice />
      <StatusFooter />
    </AuthCard>
  );
};



const SetNewPassword = ({ code, username }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState(null);

  const { mutate, isLoading } = useMutation(setNewPasswordService, {
    onSuccess: () =>
      navigate('/login', {
        state: { notice: 'Password reset successfully — please sign in.' },
      }),
    onError: (err) => {
      if (err?.message === 'Code expired') {
        setServerError({ text: 'This reset link has expired.', expired: true });
      } else {
        setServerError({
          text: err?.message || 'Could not reset password. Please try again.',
          expired: false,
        });
      }
    },
  });

  const validationError = validateNewPassword(password, confirm);

  const clientHint = confirm.length > 0 ? validationError : null;
  const displayedError = serverError?.text || clientHint;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validationError) return;
    setServerError(null);
    mutate({ username, code, password });
  };

  return (
    <AuthCard as="form" onSubmit={handleSubmit} $center>
      <LogoLine>
        <Logo />
      </LogoLine>
      <CenteredTitle>Reset Password</CenteredTitle>
      <CenteredSubtitle>
        Enter a new password for <strong>{username}</strong>
      </CenteredSubtitle>

      <FieldGroup>
        <FieldLabel htmlFor="new-password">New Password</FieldLabel>
        <InputShell>
          <TextInput
            id="new-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter a new password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setServerError(null);
            }}
            autoComplete="new-password"
            $hasTrailing
          />
          <TrailingButton
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </TrailingButton>
        </InputShell>
      </FieldGroup>

      <FieldGroup $gap={14}>
        <FieldLabel htmlFor="confirm-password">Confirm Password</FieldLabel>
        <InputShell>
          <TextInput
            id="confirm-password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter the new password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setServerError(null);
            }}
            autoComplete="new-password"
            $hasTrailing
          />
          <TrailingButton
            type="button"
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
            onClick={() => setShowConfirm((prev) => !prev)}
          >
            {showConfirm ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </TrailingButton>
        </InputShell>
      </FieldGroup>

      {displayedError && (
        <FeedbackText $type="error">
          {displayedError}
          {serverError?.expired && (
            <>
              {' '}
              <InlineLink type="button" onClick={() => navigate('/reset-password')}>
                Request a new reset email
              </InlineLink>
            </>
          )}
        </FeedbackText>
      )}

      <PrimaryButton type="submit" disabled={isLoading || Boolean(validationError)}>
        <ArrowRightIcon size={16} />
        <span>Reset Password</span>
      </PrimaryButton>

      <BackLink type="button" onClick={() => navigate('/login')}>
        Back to sign in
      </BackLink>

      <AuthorizedNotice />
      <StatusFooter />
    </AuthCard>
  );
};



const Page = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: ${colors.bg};
  overflow: hidden;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      121deg,
      rgba(5, 8, 15, 0.32) 7.7%,
      rgba(5, 8, 15, 0.62) 41.5%,
      rgba(5, 8, 15, 0.78) 92.3%
    ),
    url(${AUTH_BG});
  background-size: cover;
  background-position: center;
  z-index: 0;
`;

const TopLogo = styled.div`
  position: absolute;
  top: 50px;
  left: 60px;
  z-index: 2;

  @media (max-width: 640px) {
    top: 28px;
    left: 28px;
  }
`;

const CardWrap = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
`;

const LogoLine = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const CenteredTitle = styled(CardTitle)`
  width: 100%;
  text-align: center;
`;

const CenteredSubtitle = styled(CardSubtitle)`
  width: 100%;
  text-align: center;
`;

const FeedbackText = styled.p`
  width: 100%;
  margin-top: 14px;
  font-family: ${fonts.body};
  font-size: 12px;
  text-align: center;
  color: ${({ $type }) => ($type === 'success' ? colors.green : colors.red)};
`;

const InlineLink = styled.button`
  font-family: ${fonts.body};
  font-size: 12px;
  color: ${colors.cyan};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${colors.ink};
  }
`;

const BackLink = styled.button`
  width: 100%;
  margin-top: 16px;
  font-family: ${fonts.mono};
  font-weight: 400;
  font-size: 12px;
  letter-spacing: 0.48px;
  color: ${colors.muted};
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.cyan};
  }
`;
